import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Check, CloudOff, FilePlus2, Loader2, Wand2 } from "lucide-react";
import CreatorShell from "@/components/creator/CreatorShell";
import GenrePicker from "@/components/creator/music/GenrePicker";
import StyleHelper from "@/components/creator/music/StyleHelper";
import VocalPanel from "@/components/creator/music/VocalPanel";
import ArrangementPanel from "@/components/creator/music/ArrangementPanel";
import LyricsField from "@/components/creator/music/LyricsField";
import MusicControls from "@/components/creator/music/MusicControls";
import ModelRow from "@/components/creator/music/ModelRow";
import StartModePicker from "@/components/creator/music/StartModePicker";
import VoiceHealthBanner from "@/components/sunovoice/VoiceHealthBanner";

const GENERATE_LABEL = { scratch: "GENERATE MY SONG", add_band: "ADD THE BAND", add_singing: "ADD THE SINGING", grow_loop: "GROW MY SONG" };
import useSunoVoices from "@/components/sunovoice/useSunoVoices";
import { defaultVoice } from "@/lib/sunoVoicesApi";
import useProjectDraft, { rememberDraftId } from "@/components/creator/music/useProjectDraft";
import { fmtAgo, generateProject, getCatalog, listVoiceProfiles, listVoices } from "@/lib/studioApi";
import { safeApiError } from "@/lib/safeErrorText";

function SaveStatus({ save, projectId }) {
  const map = {
    idle: { Icon: FilePlus2, text: "Starts saving as soon as you type", cls: "text-white/40" },
    saving: { Icon: Loader2, text: "Saving…", cls: "text-white/60", spin: true },
    saved: { Icon: Check, text: `Saved${save.at ? ` · ${fmtAgo(save.at)}` : ""}`, cls: "text-emerald-300/90" },
    error: { Icon: CloudOff, text: save.error || "Not saved", cls: "text-rose-300" },
  };
  const s = map[save.state] || map.idle;
  return (
    <p data-testid="draft-save-status" data-state={save.state} data-project-id={projectId || ""} className={`flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] ${s.cls}`}>
      <s.Icon className={`h-3.5 w-3.5 ${s.spin ? "animate-spin" : ""}`} /> {s.text}
    </p>
  );
}

function sunoVoiceBlocker(form, sunoVoices) {
  const id = form.engine?.custom_model_id;
  if (!id || !sunoVoices) return null;
  const v = sunoVoices.find((x) => x.id === id);
  if (!v) return "That ELPM voice was deleted — pick another model.";
  if (v.status === "broken") return `“${v.name}” stopped working on the music engine — re-make it in My Voices, or pick V6 for now.`;
  if (v.status !== "ready") return `“${v.name}” isn't ready yet — finish it in My Voices, or pick another model.`;
  if (form.vocal.source === "my_voice") return "Your ELPM voice already sings in your voice — switch the lead vocal back to AI Gospel Voice.";
  return null;
}

function generateBlocker(form, maxChars, sunoVoices, sourceAudio) {
  const mode = form.start_mode || "scratch";
  const instrumental = form.vocal.mode === "instrumental";
  const lyrics = form.lyrics || "";
  if (mode !== "scratch" && sourceAudio?.status !== "ready") return "Upload your song file first.";
  if (form.genre_key === "custom" && !(form.custom_style || "").trim()) return "Describe your custom style first.";
  if (mode === "add_band") return null;
  if (mode === "add_singing" && instrumental) return "Pick who sings — Instrumental has no singing to add.";
  if (mode === "grow_loop" && !instrumental && !lyrics.trim()) return "Write the lyrics — or pick Instrumental to sing over it yourself later.";
  if (instrumental) return null;
  const voiceIssue = sunoVoiceBlocker(form, sunoVoices);
  if (voiceIssue) return voiceIssue;
  if (lyrics.length > maxChars) return `Lyrics are ${lyrics.length} characters — the limit is ${maxChars}.`;
  if (mode === "add_singing" && !lyrics.trim()) return "Paste the lyrics ELPM should sing over your music.";
  if (form.lyrics_lock && !lyrics.trim()) return "Lyrics Lock is on — paste your lyrics (or turn the lock off).";
  if (!lyrics.trim() && !(form.title || "").trim()) return "Add lyrics or at least a title.";
  const myVoice = form.vocal.mode === "signature" && form.vocal.source === "my_voice";
  if (mode === "scratch" && myVoice && ["duet", "choir"].includes(form.arrangement?.type)) return "My Recorded Voice can't be used for a duet or choir song — switch to AI Gospel Voice.";
  if (myVoice && !form.vocal.voice_id) return "Choose your recorded voice.";
  return null;
}

function CreateMusicForm({ catalog }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialId = params.get("project");
  const styleParam = params.get("style");
  const draft = useProjectDraft({
    initialId, catalog, styleParam,
    onCreated: (id) => setParams((p) => { const n = new URLSearchParams(p); n.set("project", id); n.delete("style"); return n; }, { replace: true }),
  });
  const { form, update, save, projectId } = draft;
  const [voices, setVoices] = useState(null);
  const [profiles, setProfiles] = useState(null);
  useEffect(() => { listVoiceProfiles().then(setProfiles).catch(() => setProfiles([])); }, []);
  const [busy, setBusy] = useState(false);
  const [lyricsBusy, setLyricsBusy] = useState(false);
  const { voices: sunoVoices, reload: reloadSunoVoices } = useSunoVoices();
  useEffect(() => { listVoices().then(setVoices).catch(() => setVoices([])); }, []);

  // "Make a song with this voice" (?voice=<suno voice id>) → preselect it in the model menu.
  const voiceParam = params.get("voice");
  const formReady = Boolean(form);
  useEffect(() => {
    if (!formReady || !voiceParam) return;
    const f = draft.form;
    update({ engine: { model: f.engine?.model || "V6", custom_model_id: voiceParam }, vocal: { ...f.vocal, mode: "signature", source: "ai" } });
    setParams((p) => { const n = new URLSearchParams(p); n.delete("voice"); return n; }, { replace: true });
  }, [formReady, voiceParam]); // eslint-disable-line react-hooks/exhaustive-deps

  // Every NEW (fresh) song form starts with your default ELPM voice picked, once; you can still change it.
  const appliedForRef = useRef(-1);
  useEffect(() => {
    if (!formReady || projectId || voiceParam || !sunoVoices || appliedForRef.current === draft.freshId) return;
    appliedForRef.current = draft.freshId;
    const d = defaultVoice(sunoVoices);
    const f = draft.form;
    if (!d || f.engine?.custom_model_id || f.vocal.mode !== "signature") return;
    update({ engine: { model: f.engine?.model || "V6", custom_model_id: d.id }, vocal: { ...f.vocal, source: "ai" } });
  }, [formReady, projectId, sunoVoices, voiceParam, draft.freshId]); // eslint-disable-line react-hooks/exhaustive-deps

  const allGenres = useMemo(() => [...catalog.gospel_genres, ...catalog.more_genres], [catalog]);
  if (!form) return <p className="flex items-center gap-2 text-white/60"><Loader2 className="h-4 w-4 animate-spin" /> Opening your project…</p>;

  const genre = allGenres.find((g) => g.key === form.genre_key) || null;
  const structure = catalog.structures.find((s) => s.key === form.structure);
  const mode = form.start_mode || "scratch";
  const blocker = generateBlocker(form, catalog.max_lyrics_chars, sunoVoices, draft.sourceAudio);
  const instrumental = form.vocal.mode === "instrumental";

  const handleGenerate = async () => {
    if (blocker) { toast.error("Almost there", { description: blocker }); return; }
    setBusy(true);
    try {
      const id = await draft.persist(true);
      const p = await generateProject(id);
      rememberDraftId(null);
      toast.success(`Creating "${p.title || "your song"}"`, { description: "It keeps going even if you close this page." });
      navigate(`/create/project/${p.id}`);
    } catch (e) {
      const voiceId = form.engine?.custom_model_id;
      const broken = e?.response?.status === 409 && voiceId && /re-made/.test(safeApiError(e, ""));
      if (broken) reloadSunoVoices();
      toast.error(broken ? "Your ELPM voice needs re-making" : "We couldn't start the song", {
        description: safeApiError(e, e?.message || "Please try again."),
        ...(broken ? { action: { label: "Re-make voice", onClick: () => navigate(`/create/my-voices/elpm/${voiceId}`) } } : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_380px]">
      <div className="min-w-0 space-y-9">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-300/80">Create Music</p>
          <h1 data-testid="creator-title" className="mt-3 font-display text-3xl font-bold sm:text-4xl lg:text-5xl">Your song, your words</h1>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <SaveStatus save={save} projectId={projectId} />
            {draft.resumed ? <span data-testid="draft-resumed" className="font-mono text-[10px] uppercase tracking-[0.16em] text-amber-200/80">Continuing your last draft</span> : null}
            {projectId ? (
              <button type="button" data-testid="draft-start-new" onClick={() => { draft.startNew(); setParams({}, { replace: true }); }} className="font-mono text-[10px] uppercase tracking-[0.16em] text-white/45 underline-offset-4 hover:text-white hover:underline">
                Start a new song
              </button>
            ) : null}
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-[1fr_240px]">
          <label className="block">
            <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.16em] text-white/70">Song title</span>
            <input data-testid="song-title-input" value={form.title} onChange={(e) => update({ title: e.target.value })} maxLength={120} placeholder="Name your song" className="w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-3.5 text-base text-white placeholder:text-white/30 focus:border-blue-400/60 focus:outline-none" />
          </label>
          <label className="block">
            <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.16em] text-white/70">Artist</span>
            <input data-testid="song-artist-input" value={form.artist} onChange={(e) => update({ artist: e.target.value })} maxLength={80} placeholder="Apostle Moore" className="w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-3.5 text-base text-white placeholder:text-white/30 focus:border-blue-400/60 focus:outline-none" />
          </label>
        </div>
        <VoiceHealthBanner voices={sunoVoices} />
        <StartModePicker mode={mode} onMode={(m) => update({ start_mode: m })} projectId={projectId} ensureProject={() => draft.persist(true)}
          source={draft.sourceAudio} onSource={draft.setSourceAudio} maxSec={catalog.max_upload_sec || 480} />
        {mode !== "add_band" ? <LyricsField form={form} structure={structure} maxChars={catalog.max_lyrics_chars} genreLabel={genre?.label || form.custom_style || "Gospel"} instrumental={instrumental} onChange={update} onBusyChange={setLyricsBusy} busy={lyricsBusy || busy} /> : null}
        <StyleHelper catalog={catalog} form={form} onChange={update} />
        <GenrePicker catalog={catalog} genreKey={form.genre_key} customStyle={form.custom_style} onChange={update} />
        {mode !== "add_band" ? <VocalPanel catalog={catalog} vocal={form.vocal} vocalStyle={form.vocal_style} voices={voices} profiles={profiles} arrangementType={form.arrangement?.type} onChange={update} /> : null}
        {!instrumental && mode === "scratch" ? <ArrangementPanel catalog={catalog} form={form} profiles={profiles} onChange={update} /> : null}
      </div>

      <aside className="lg:sticky lg:top-36 lg:self-start">
        <div className="rounded-3xl border border-white/[0.08] bg-[#0a0d1c]/85 p-6 backdrop-blur-xl">
          <p className="mb-5 font-display text-sm font-semibold text-white/85">Music settings</p>
          <div className="mb-6"><ModelRow form={form} sunoVoices={sunoVoices} oldVoices={voices} onChange={update} /></div>
          <MusicControls catalog={catalog} form={form} genre={genre} onChange={update} mode={mode} />
          <button
            type="button"
            data-testid="generate-song-btn"
            onClick={handleGenerate}
            disabled={busy || lyricsBusy}
            className="group mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-gradient-to-r from-blue-500 to-violet-600 px-6 py-4 font-display text-base font-bold tracking-wide text-white shadow-[0_14px_44px_rgba(99,102,241,0.45)] transition-transform duration-200 hover:scale-[1.015] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Wand2 className="h-5 w-5 transition-transform group-hover:rotate-12" />}
            {GENERATE_LABEL[mode]}
          </button>
          {blocker ? (
            <p data-testid="generate-blocker" className="mt-3 flex items-start gap-1.5 text-[12px] text-amber-200/85"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{blocker}</p>
          ) : (
            <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.16em] text-white/35">Two versions · ready in about 1–3 min · saved to My Music Library</p>
          )}
        </div>
      </aside>
    </div>
  );
}

/** Create Music — the full generator (genres, signature voice, Lyrics Lock), autosaved as a project. */
export default function CreateSongPage() {
  const [catalog, setCatalog] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => { getCatalog().then(setCatalog).catch((e) => setError(safeApiError(e, "Couldn't load the studio settings."))); }, []);
  return (
    <CreatorShell testId="create-song-page" back="/create" spine="write" wide>
      {catalog ? <CreateMusicForm catalog={catalog} />
        : error ? <p data-testid="create-music-error" className="text-rose-300">{error}</p>
          : <p className="flex items-center gap-2 text-white/60"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>}
    </CreatorShell>
  );
}
